import React, { useState, useEffect, useRef, useMemo, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import CallAlert from './components/CallAlert';
import api from './lib/api';
import Login from './pages/Login';

// Lazy loading all modules
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Patients = lazy(() => import('./pages/Patients'));
const OdontogramView = lazy(() => import('./pages/OdontogramView'));
const Appointments = lazy(() => import('./pages/Appointments'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Treatments = lazy(() => import('./pages/Treatments'));
const CashRegister = lazy(() => import('./pages/CashRegister'));
const Stock = lazy(() => import('./pages/Stock'));
const Prescriptions = lazy(() => import('./pages/Prescriptions'));
const WhatsAppSettings = lazy(() => import('./pages/WhatsAppSettings'));
const Settings = lazy(() => import('./pages/Settings'));
const WaitingRoom = lazy(() => import('./pages/WaitingRoom'));
const Facturation = lazy(() => import('./pages/Facturation'));
const TechnicianDashboard = lazy(() => import('./pages/TechnicianDashboard'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const Staff = lazy(() => import('./pages/Staff'));
const LabOrders = lazy(() => import('./pages/LabOrders'));

// Rôles backend : superadmin, admin, dentiste, assistante, secretaire, technicien
const ADMIN_ACCESS_ROLES = ['admin', 'secretaire'];

// Onglets visibles dans le layout admin selon le rôle (undefined = tous)
const ADMIN_TABS_BY_ROLE = {
  secretaire: ['dashboard', 'patients', 'salle-attente', 'rdv', 'facturation'],
};

const ROLE_LABELS = {
  superadmin: 'SuperAdmin',
  admin: 'Administrateur',
  dentiste: 'Dentiste',
  assistante: 'Assistante Dentaire',
  secretaire: 'Secrétaire / Accueil',
  technicien: 'Technicien Dentaire',
};

function roleLabel(role) {
  return ROLE_LABELS[role] || 'Utilisateur';
}

function homeForRole(role) {
  if (role === 'superadmin') return '/superadmin/dashboard';
  if (ADMIN_ACCESS_ROLES.includes(role)) return '/admin/dashboard';
  return '/technicien/dashboard';
}

// Navigation — items avec Mon Labo & Stock/Fournisseurs
const TABS_ADMIN = [
  { id: 'dashboard',     label: 'Tableau de bord',       icon: '⊞',  path: '/admin/dashboard' },
  { id: 'patients',      label: 'Patients',               icon: '👥', path: '/admin/patients' },
  { id: 'salle-attente', label: 'Salle d\'Attente',      icon: '⏱️', path: '/admin/salle-attente' },
  { id: 'rdv',           label: 'Agenda',                 icon: '📅', path: '/admin/rdv' },
  { id: 'facturation',   label: 'Facturation',            icon: '🧾', path: '/admin/facturation' },
  { id: 'stock',         label: 'Stock & Fournisseurs',  icon: '📦', path: '/admin/stock' },
  { id: 'labo',          label: 'Mon Labo',               icon: '🔬', path: '/admin/labo' },
  { id: 'staff',         label: 'Staff & Équipe',         icon: '👨‍⚕️', path: '/admin/staff' },
];

const TABS_TECH = [
  { id: 'dashboard', label: 'Tableau de bord', icon: '⊞', path: '/technicien/dashboard' },
  { id: 'rdv', label: 'Mes RDV', icon: '📅', path: '/technicien/rdv' },
  { id: 'patients', label: 'Patients', icon: '👥', path: '/technicien/patients' },
  { id: 'odontogram', label: 'Odontogramme', icon: '🦷', path: '/technicien/odontogram' },
];

const TABS_SUPERADMIN = [
  { id: 'dashboard', label: 'Tableau de bord', icon: '⊞', path: '/superadmin/dashboard' },
  { id: 'cabinets', label: 'Cabinets', icon: '🏥', path: '/superadmin/cabinets' },
  { id: 'utilisateurs', label: 'Utilisateurs', icon: '👤', path: '/superadmin/utilisateurs' },
];

// ============================================================
// CABINET SWITCHER — Multi-Cabinets ODENTIS
// ============================================================
function CabinetSwitcher({ user, compact = false }) {
  const { switchCabinet } = useAuth();
  const [open, setOpen] = useState(false);
  const [cabinets, setCabinets] = useState([]);
  const [switching, setSwitching] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    api.get('/cabinets').then(res => {
      if (Array.isArray(res.data)) {
        setCabinets(res.data);
      }
    }).catch(() => {});
  }, [user?.tenantId]);

  const activeCabinet = cabinets.find(c => c.id === user?.tenantId) || {
    id: user?.tenantId,
    name: user?.tenantName || user?.tenantSettings?.name || 'Cabinet Dentaire'
  };

  // Si l'utilisateur n'a qu'un seul cabinet (ou est une assistante/secrétaire assignée à 1 cabinet)
  // On affiche un badge élégant et statique sans menu déroulant
  if (cabinets.length <= 1) {
    return (
      <div
        title={activeCabinet.name}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: compact ? '4px 8px' : '6px 12px',
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.22)',
          borderRadius: 10, color: '#fff',
          fontSize: 12.5, fontWeight: 700
        }}
      >
        <span style={{ fontSize: 13 }}>🏥</span>
        {!compact && (
          <span className="cabinet-name-mobile-hide" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activeCabinet.name}
          </span>
        )}
      </div>
    );
  }

  // Si le dentiste dispose de plusieurs cabinets (ex: Salam & Anza)
  const handleSelect = async (c) => {
    if (c.id === user?.tenantId) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await switchCabinet(c.id);
      setOpen(false);
      window.location.reload();
    } catch (err) {
      console.error('Error switching cabinet:', err);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Changer de cabinet actif"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: compact ? '4px 8px' : '6px 12px',
          background: open ? 'rgba(255,255,255,0.24)' : 'rgba(255,255,255,0.14)',
          border: '1px solid rgba(255,255,255,0.28)',
          borderRadius: 10, cursor: 'pointer', color: '#fff',
          fontSize: 12.5, fontWeight: 700, transition: 'all 0.18s'
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
      >
        <span style={{ fontSize: 13 }}>🏥</span>
        {!compact && (
          <span className="cabinet-name-mobile-hide" style={{ maxWidth: 175, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activeCabinet.name}
          </span>
        )}
        <span style={{ fontSize: 9, opacity: 0.85, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</span>
      </button>

      {open && (
        <div className="cabinet-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: 290, background: '#fff', borderRadius: 14,
          boxShadow: '0 20px 45px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #e2e8f0', overflow: 'hidden', zIndex: 10000,
          animation: 'dropIn 0.15s ease-out'
        }}>
          <div style={{ padding: '12px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Vos Cabinets ({cabinets.length})
            </div>
            <div style={{ fontSize: 11.5, color: '#334155', marginTop: 2 }}>
              Sélectionnez le cabinet de travail :
            </div>
          </div>

          <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
            {cabinets.map(cab => {
              const isActive = cab.id === user?.tenantId;
              return (
                <button
                  key={cab.id}
                  disabled={switching}
                  onClick={() => handleSelect(cab)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '9px 12px', borderRadius: 8, border: 'none',
                    background: isActive ? '#eff6ff' : 'transparent',
                    color: isActive ? '#2563eb' : '#1e293b',
                    fontSize: 12.5, fontWeight: isActive ? 700 : 500,
                    textAlign: 'left', cursor: 'pointer', transition: 'background 0.12s'
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f1f5f9'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>🏥</span>
                    <div>
                      <div style={{ fontWeight: isActive ? 800 : 600 }}>{cab.name}</div>
                      {(cab.address || cab.city) && (
                        <div style={{ fontSize: 10.5, color: '#94a3b8' }}>
                          {[cab.address, cab.city].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  {isActive && <span style={{ color: '#2563eb', fontWeight: 800, fontSize: 13 }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// USER PROFILE DROPDOWN — WhatsApp hidden here
// ============================================================
function UserProfileDropdown({ user, onLogout, navigate, compact = false }) {
  const [open, setOpen] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const rawName = user?.name || user?.username || 'Praticien';
  const cleanDisplayName = rawName.replace(/^(dr\.?\s*)+/i, '').trim();
  const initials = (cleanDisplayName[0] || 'D').toUpperCase();
  const role = roleLabel(user?.role);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Avatar Button */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Profil utilisateur"
        style={{
          display: 'flex', alignItems: 'center', gap: compact ? 4 : 10,
          padding: compact ? '4px 6px 4px 4px' : '7px 14px 7px 8px',
          background: open ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.10)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: compact ? 22 : 12, cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 800, color: '#fff',
          boxShadow: '0 2px 8px rgba(37,99,235,0.4)'
        }}>
          {initials}
        </div>
        {!compact && (
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              {user?.titlePrefix ? `${user.titlePrefix} ` : ''}{cleanDisplayName}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              {role}
            </div>
          </div>
        )}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2.5" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', marginLeft: compact ? 2 : 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="cabinet-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0,
          width: 300, background: '#fff', borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9', overflow: 'hidden', zIndex: 9999,
          animation: 'dropIn 0.2s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          {/* Profile Header */}
          <div style={{
            padding: '20px 20px 16px',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderBottom: '1px solid #e0f2fe'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, fontWeight: 800, color: '#fff',
                boxShadow: '0 4px 12px rgba(37,99,235,0.3)'
              }}>
                {initials}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                  {user?.titlePrefix ? `${user.titlePrefix} ` : ''}{cleanDisplayName}
                </div>
              </div>
            </div>
            {/* Licence badge — réservé aux dentistes et administrateurs */}
            {['dentiste', 'admin', 'superadmin'].includes(user?.role) && (
              <div style={{
                marginTop: 14, padding: '10px 14px',
                background: '#fff', borderRadius: 10, border: '1px solid #dbeafe',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    🏆 Licence
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
                    Annuelle Professional
                  </div>
                </div>
                <span style={{
                  background: '#dcfce7', color: '#166534', fontSize: 11,
                  fontWeight: 700, padding: '4px 10px', borderRadius: 20,
                  border: '1px solid #bbf7d0'
                }}>
                  ✅ ACTIVE
                </span>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div style={{ padding: '8px' }}>
            {[
              ...(user?.role !== 'secretaire' ? [
                { icon: '💬', label: 'Connexion WhatsApp', action: () => { navigate('/admin/whatsapp'); setOpen(false); } },
              ] : []),
              { icon: '🔒', label: 'Modifier mon mot de passe', action: () => { navigate('/admin/settings?tab=security'); setOpen(false); } },
              ...(user?.role !== 'secretaire' ? [
                { icon: '⚙️', label: 'Paramètres du cabinet', action: () => { navigate('/admin/settings?tab=cabinet'); setOpen(false); } },
              ] : []),
            ].map((item, i) => (
              <button
                key={i}
                onClick={item.action}
                style={{
                  width: '100%', textAlign: 'left', padding: '11px 14px',
                  borderRadius: 10, border: 'none', background: 'none',
                  cursor: 'pointer', fontSize: 13.5, color: '#334155',
                  fontWeight: 500, display: 'flex', alignItems: 'center', gap: 10,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#1e40af'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#334155'; }}
              >
                <span style={{ fontSize: 16 }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>

          <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
            <button
              onClick={() => { onLogout(); setOpen(false); }}
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 10,
                border: 'none', background: '#fef2f2', cursor: 'pointer',
                fontSize: 13.5, fontWeight: 700, color: '#dc2626',
                display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
              onMouseLeave={e => e.currentTarget.style.background = '#fef2f2'}
            >
              <span style={{ fontSize: 16 }}>🚪</span>
              Se déconnecter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// NOTIFICATION DROPDOWN
// ============================================================
function NotificationDropdown({ navigate }) {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([
    {
      id: 'rdv-today',
      title: 'Consultations du jour',
      message: 'Consultez les rendez-vous programmés aujourd’hui dans votre agenda.',
      time: 'Aujourd’hui',
      icon: '📅',
      color: '#2563eb',
      bg: '#eff6ff',
      link: '/admin/rdv',
      unread: true,
    },
    {
      id: 'stock-alert',
      title: 'Alerte stock consommables',
      message: 'Des articles et consommables approchent de leur seuil minimal.',
      time: 'Il y a 2h',
      icon: '⚠️',
      color: '#d97706',
      bg: '#fffbeb',
      link: '/admin/stock',
      unread: true,
    },
    {
      id: 'inv-pending',
      title: 'Factures en attente',
      message: 'Des devis et factures nécessitent un encaissement ou un suivi.',
      time: 'Hier',
      icon: '🧾',
      color: '#7c3aed',
      bg: '#f5f3ff',
      link: '/admin/facturation?tab=factures',
      unread: true,
    },
    {
      id: 'wa-status',
      title: 'Rappels WhatsApp',
      message: 'Le module de notifications patients WhatsApp est configuré.',
      time: 'Actif',
      icon: '💬',
      color: '#16a34a',
      bg: '#f0fdf4',
      link: '/admin/whatsapp',
      unread: false,
    },
  ]);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
    setUnreadCount(0);
  };

  const handleItemClick = (n) => {
    setNotifications(prev => prev.map(item => item.id === n.id ? { ...item, unread: false } : item));
    setUnreadCount(prev => Math.max(0, prev - 1));
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Notifications"
        style={{
          width: 38, height: 38, borderRadius: 10,
          background: open ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', fontSize: 16, position: 'relative',
          transition: 'all 0.15s',
          boxShadow: open ? '0 0 0 2px rgba(255,255,255,0.25)' : 'none'
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -3, right: -3,
            minWidth: 16, height: 16, padding: '0 4px',
            background: '#ef4444', color: '#fff',
            borderRadius: 8, fontSize: 10, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #0f1f5c',
            boxShadow: '0 2px 6px rgba(239,68,68,0.5)'
          }}>
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="cabinet-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0,
          width: 340, background: '#fff', borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9', overflow: 'hidden', zIndex: 9999,
          animation: 'dropIn 0.2s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #0f1f5c 0%, #1a3a9c 100%)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>🔔</span>
              <span style={{ fontWeight: 700, fontSize: 13.5 }}>Notifications</span>
              {unreadCount > 0 && (
                <span style={{
                  background: '#f97316', color: '#fff', fontSize: 10,
                  fontWeight: 800, padding: '2px 7px', borderRadius: 10
                }}>
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                  fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 6,
                  cursor: 'pointer', transition: 'background 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {notifications.map((item) => (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                style={{
                  padding: '12px 16px',
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  borderBottom: '1px solid #f8fafc',
                  cursor: 'pointer',
                  background: item.unread ? '#f8fafc' : '#fff',
                  transition: 'background 0.15s',
                  position: 'relative'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = item.unread ? '#f8fafc' : '#fff'}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: item.bg, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 15, flexShrink: 0,
                  border: `1px solid ${item.color}30`
                }}>
                  {item.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: item.unread ? 700 : 600, color: '#0f172a' }}>
                      {item.title}
                    </div>
                    <span style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      {item.time}
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2, lineHeight: 1.4 }}>
                    {item.message}
                  </div>
                </div>
                {item.unread && (
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: '#2563eb', flexShrink: 0, alignSelf: 'center'
                  }} />
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
            textAlign: 'center'
          }}>
            <button
              onClick={() => { setOpen(false); navigate('/admin/rdv'); }}
              style={{
                background: 'none', border: 'none', color: '#2563eb',
                fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: 0
              }}
            >
              Voir tout l'agenda & activités →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MOBILE BOTTOM NAVIGATION BAR
// ============================================================
function MobileBottomNav({ role, activeTab, onTabChange }) {
  let items = [];

  if (role === 'admin') {
    items = [
      { id: 'dashboard', label: 'Accueil', icon: '⊞' },
      { id: 'patients', label: 'Patients', icon: '👥' },
      { id: 'salle-attente', label: 'Attente', icon: '⏱️' },
      { id: 'rdv', label: 'Agenda', icon: '📅' },
      { id: 'facturation', label: 'Factures', icon: '🧾' },
    ];
  } else if (role === 'superadmin') {
    items = [
      { id: 'dashboard', label: 'Accueil', icon: '⊞' },
      { id: 'cabinets', label: 'Cabinets', icon: '🏥' },
      { id: 'utilisateurs', label: 'Utilisateurs', icon: '👤' },
    ];
  } else {
    items = [
      { id: 'dashboard', label: 'Accueil', icon: '⊞' },
      { id: 'rdv', label: 'Mes RDV', icon: '📅' },
      { id: 'patients', label: 'Patients', icon: '👥' },
      { id: 'odontogram', label: 'Dents', icon: '🦷' },
    ];
  }

  return (
    <nav
      className="nav-mobile-bottom"
      aria-label="Navigation mobile"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 64,
        background: 'rgba(255, 255, 255, 0.96)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '0 4px',
        zIndex: 900,
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.07)',
      }}
    >
      {items.map(item => {
        const isActive = activeTab === item.id;
        return (
          <button
            key={item.id}
            onClick={() => onTabChange(item.id)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              padding: '6px 2px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: isActive ? '#2563eb' : '#64748b',
              transition: 'all 0.15s ease',
              position: 'relative',
            }}
          >
            {isActive && (
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  width: 24,
                  height: 3,
                  background: '#2563eb',
                  borderRadius: 3,
                }}
              />
            )}
            <span style={{ fontSize: 18, lineHeight: 1 }}>{item.icon}</span>
            <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: '-0.2px' }}>
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// ============================================================
// PREMIUM NAVBAR
// ============================================================
function PremiumNav({ tabs, activeTab, onTabChange, user, onLogout }) {
  const navigate = useNavigate();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const rawName = user?.name || user?.username || 'Cabinet';
  const cleanDisplayName = rawName.replace(/^(dr\.?\s*)+/i, '').trim();
  const initials = (cleanDisplayName[0] || 'D').toUpperCase();
  const roleName = roleLabel(user?.role);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes dropIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, sans-serif; margin: 0; }
        nav::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
        nav { -ms-overflow-style: none !important; scrollbar-width: none !important; }
        .nav-tab {
          padding: 8px 14px; border-radius: 8px; font-weight: 600; font-size: 12.5px;
          color: rgba(255,255,255,0.65); background: none; border: none; white-space: nowrap;
          cursor: pointer; transition: all 0.18s; letter-spacing: 0.1px;
          display: flex; align-items: center; gap: 0;
          font-family: 'Inter', sans-serif;
        }
        .nav-tab:hover { color: #fff; background: rgba(255,255,255,0.10); }
        .nav-tab.active {
          color: #fff; background: rgba(255,255,255,0.15);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.2);
        }
        .nav-tab.active::after {
          content: ''; position: absolute; bottom: -2px; left: 50%; transform: translateX(-50%);
          width: 20px; height: 2px; background: #60a5fa; border-radius: 2px;
        }
        .nav-tab { position: relative; }

        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, sans-serif; margin: 0; }
        nav::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
        nav { -ms-overflow-style: none !important; scrollbar-width: none !important; }
        .nav-tab {
          padding: 8px 14px; border-radius: 8px; font-weight: 600; font-size: 12.5px;
          color: rgba(255,255,255,0.65); background: none; border: none; white-space: nowrap;
          cursor: pointer; transition: all 0.18s; letter-spacing: 0.1px;
          display: flex; align-items: center; gap: 0;
          font-family: 'Inter', sans-serif;
        }
        .nav-tab:hover { color: #fff; background: rgba(255,255,255,0.10); }
        .nav-tab.active {
          color: #fff; background: rgba(255,255,255,0.15);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.2);
        }
        .nav-tab.active::after {
          content: ''; position: absolute; bottom: -2px; left: 50%; transform: translateX(-50%);
          width: 20px; height: 2px; background: #60a5fa; border-radius: 2px;
        }
        .nav-tab { position: relative; }

        @media (max-width: 900px) {
          .nav-desktop-tabs { display: none !important; }
          .nav-desktop-user { display: none !important; }
          .nav-desktop-sep { display: none !important; }
          .nav-mobile-btn { display: flex !important; }
          .nav-mobile-bottom { display: flex !important; }
        }
        @media (min-width: 901px) {
          .nav-mobile-btn { display: none !important; }
          .nav-mobile-bottom { display: none !important; }
        }
      `}</style>
      <header className="odentis-header" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 12px', height: 58, gap: 8,
        background: 'linear-gradient(135deg, #0f1f5c 0%, #1a3a9c 45%, #0d7a78 100%)',
        position: 'sticky', top: 0, zIndex: 200,
        boxShadow: '0 2px 20px rgba(15,31,92,0.5)',
      }}>
        {/* Left side: Mobile Hamburger Button + Logo + Desktop Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Mobile Hamburger Button on the LEFT (styled cleanly like ArchFlow reference) */}
          <button
            className="nav-mobile-btn"
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Menu principal"
            style={{
              width: 38, height: 38, borderRadius: 10,
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.22)',
              color: '#fff', display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', fontSize: 18,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.20)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
          >
            ☰
          </button>

          {/* Logo */}
          <div
            onClick={() => navigate(tabs[0]?.path || '/')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: 'pointer', flexShrink: 0,
              padding: '6px 0'
            }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: 'linear-gradient(135deg, #60a5fa, #34d399)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 17, boxShadow: '0 2px 10px rgba(96,165,250,0.5)'
            }}>🦷</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '-0.3px' }}>
                ODENTIS
              </div>
            </div>
          </div>

          {/* Desktop Separator */}
          <div className="nav-desktop-sep" style={{ width: 1, height: 30, background: 'rgba(255,255,255,0.12)', margin: '0 6px' }} />

          {/* Desktop Nav tabs */}
          <nav className="nav-desktop-tabs" style={{
            display: 'flex', gap: 2, overflowX: 'auto',
            alignItems: 'center', scrollbarWidth: 'none', msOverflowStyle: 'none'
          }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`nav-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => onTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Right side: Cabinet Switcher, Notifications & User Avatar Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {/* Multi-Cabinet Switcher */}
          <CabinetSwitcher user={user} />

          {/* Notification dropdown */}
          <NotificationDropdown navigate={navigate} />

          {/* Desktop User dropdown (Full badge) */}
          <div className="nav-desktop-user">
            <UserProfileDropdown user={user} onLogout={onLogout} navigate={navigate} compact={false} />
          </div>

          {/* Mobile User dropdown (Compact circle [ (R) ▾ ] matching reference image) */}
          <div className="nav-mobile-btn">
            <UserProfileDropdown user={user} onLogout={onLogout} navigate={navigate} compact={true} />
          </div>
        </div>
      </header>

      <CallAlert />

      {/* MOBILE DRAWER OVERLAY (SLIDE FROM LEFT) */}
      {mobileDrawerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex' }}>
          {/* Backdrop */}
          <div
            onClick={() => setMobileDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
              animation: 'fadeIn 0.2s ease-out',
            }}
          />

          {/* Slide-in Sheet FROM LEFT */}
          <div style={{
            position: 'relative', marginRight: 'auto', width: '82%', maxWidth: 320,
            height: '100%', background: '#fff', boxShadow: '10px 0 30px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', animation: 'slideInLeft 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
            zIndex: 10000,
          }}>
            {/* Drawer Header */}
            <div style={{
              padding: '20px 18px',
              background: 'linear-gradient(135deg, #0f1f5c 0%, #1a3a9c 100%)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: 'linear-gradient(135deg, #60a5fa, #a78bfa)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, fontWeight: 800, color: '#fff',
                }}>
                  {initials}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                    {user?.titlePrefix ? `${user.titlePrefix} ` : ''}{cleanDisplayName}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                    {roleName}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setMobileDrawerOpen(false)}
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(255,255,255,0.15)', border: 'none',
                  color: '#fff', fontSize: 18, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            {/* Modules List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 10px' }}>
                Modules ODENTIS
              </div>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    onTabChange(tab.id);
                    setMobileDrawerOpen(false);
                  }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '12px 14px',
                    borderRadius: 10, border: 'none',
                    background: activeTab === tab.id ? '#eff6ff' : 'transparent',
                    color: activeTab === tab.id ? '#2563eb' : '#334155',
                    fontWeight: activeTab === tab.id ? 700 : 500, fontSize: 14,
                    display: 'flex', alignItems: 'center', gap: 10,
                    marginBottom: 4, cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 16 }}>{tab.icon || '📂'}</span>
                  <span>{tab.label}</span>
                </button>
              ))}

              <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />

              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 10px' }}>
                Outils & Paramètres
              </div>
              <button
                onClick={() => { navigate('/admin/whatsapp'); setMobileDrawerOpen(false); }}
                style={{
                  width: '100%', textAlign: 'left', padding: '12px 14px',
                  borderRadius: 10, border: 'none', background: 'transparent',
                  color: '#334155', fontWeight: 500, fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                }}
              >
                <span>💬</span>
                <span>WhatsApp Messages</span>
              </button>
              <button
                onClick={() => { navigate('/admin/settings?tab=cabinet'); setMobileDrawerOpen(false); }}
                style={{
                  width: '100%', textAlign: 'left', padding: '12px 14px',
                  borderRadius: 10, border: 'none', background: 'transparent',
                  color: '#334155', fontWeight: 500, fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                }}
              >
                <span>⚙️</span>
                <span>Paramètres Cabinet</span>
              </button>
            </div>

            {/* Logout Footer */}
            <div style={{ padding: '14px', borderTop: '1px solid #f1f5f9' }}>
              <button
                onClick={() => {
                  setMobileDrawerOpen(false);
                  onLogout();
                }}
                style={{
                  width: '100%', padding: '12px', borderRadius: 10,
                  border: 'none', background: '#fef2f2', color: '#dc2626',
                  fontWeight: 700, fontSize: 14, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
                }}
              >
                <span>🚪</span>
                <span>Se déconnecter</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// LAYOUTS
// ============================================================
function AdminLayout({ children }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const adminTabs = useMemo(() => {
    const allowed = ADMIN_TABS_BY_ROLE[user?.role];
    return TABS_ADMIN.filter(t =>
      (t.id !== 'labo' || user?.tenantSettings?.has_lab !== false) &&
      (!allowed || allowed.includes(t.id))
    );
  }, [user]);

  useEffect(() => {
    const tab = adminTabs.find(t => location.pathname === t.path || location.pathname.startsWith(t.path + '/'));
    if (tab) setActiveTab(tab.id);
  }, [location.pathname, adminTabs]);

  const handleTabChange = (tabId) => {
    const tab = adminTabs.find(t => t.id === tabId);
    if (tab) navigate(tab.path);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f4f8' }}>
      <PremiumNav
        tabs={adminTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        user={user}
        onLogout={() => { logout(); navigate('/login'); }}
      />
      <main className="odentis-main-content">
        {children}
      </main>
      <MobileBottomNav role="admin" activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}

function TechnicianLayout({ children }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const tab = TABS_TECH.find(t => location.pathname === t.path || location.pathname.startsWith(t.path + '/'));
    if (tab) setActiveTab(tab.id);
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    const tab = TABS_TECH.find(t => t.id === tabId);
    if (tab) navigate(tab.path);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f4f8' }}>
      <PremiumNav
        tabs={TABS_TECH}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        user={user}
        onLogout={() => { logout(); navigate('/login'); }}
      />
      <main className="odentis-main-content">{children}</main>
      <MobileBottomNav role="technicien" activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}

function SuperAdminLayout({ children }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const tab = TABS_SUPERADMIN.find(t => location.pathname === t.path || location.pathname.startsWith(t.path + '/'));
    if (tab) setActiveTab(tab.id);
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    const tab = TABS_SUPERADMIN.find(t => t.id === tabId);
    if (tab) navigate(tab.path);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f4f8' }}>
      {/* SuperAdmin Banner */}
      <div style={{
        padding: '8px 24px', textAlign: 'center', fontSize: 12, fontWeight: 700,
        background: 'linear-gradient(90deg, #7c3aed, #db2777)',
        color: '#fff', letterSpacing: '1px', textTransform: 'uppercase'
      }}>
        ⚡ Console SuperAdministrateur — Pilotage Central Multi-Cabinets ODENTIS SaaS
      </div>
      <PremiumNav
        tabs={TABS_SUPERADMIN}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        user={user}
        onLogout={() => { logout(); navigate('/login'); }}
      />
      <main className="odentis-main-content">{children}</main>
      <MobileBottomNav role="superadmin" activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}

// ============================================================
// PROTECTED ROUTE
// ============================================================
function ProtectedRoute({ children, allowedRoles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

// ============================================================
// ROUTES
// ============================================================
function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={!user ? <Login /> : <Navigate to={homeForRole(user.role)} replace />} />

      {/* SuperAdmin */}
      <Route path="/superadmin/*" element={
        <ProtectedRoute allowedRoles={['superadmin']}>
          <SuperAdminLayout>
            <Routes>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Suspense fallback={<Loading />}><SuperAdminDashboard section="dashboard" /></Suspense>} />
              <Route path="cabinets" element={<Suspense fallback={<Loading />}><SuperAdminDashboard section="cabinets" /></Suspense>} />
              <Route path="utilisateurs" element={<Suspense fallback={<Loading />}><SuperAdminDashboard section="users" /></Suspense>} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </SuperAdminLayout>
        </ProtectedRoute>
      } />

      {/* Admin */}
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
          <AdminLayout>
            <Routes>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Suspense fallback={<Loading />}><AdminDashboard /></Suspense>} />
              <Route path="patients" element={<Suspense fallback={<Loading />}><Patients /></Suspense>} />
              <Route path="odontogram" element={<Suspense fallback={<Loading />}><OdontogramView /></Suspense>} />
              <Route path="odontogramme" element={<Navigate to="/admin/odontogram" replace />} />
              <Route path="rdv" element={<Suspense fallback={<Loading />}><Appointments /></Suspense>} />
              <Route path="salle-attente" element={<Suspense fallback={<Loading />}><WaitingRoom /></Suspense>} />
              {/* Facturation unifiée — tab bar interne */}
              <Route path="facturation" element={<Suspense fallback={<Loading />}><Facturation /></Suspense>} />
              {/* Deep-links: redirect vers facturation avec le bon onglet */}
              <Route path="factures" element={<Navigate to="/admin/facturation?tab=factures" replace />} />
              <Route path="caisse" element={<Navigate to="/admin/facturation?tab=caisse" replace />} />
              <Route path="actes" element={<Navigate to="/admin/settings?tab=actes" replace />} />
              <Route path="stock" element={<Suspense fallback={<Loading />}><Stock /></Suspense>} />
              <Route path="labo" element={<Suspense fallback={<Loading />}><LabOrders /></Suspense>} />
              <Route path="ordonnances" element={<Suspense fallback={<Loading />}><Prescriptions /></Suspense>} />
              <Route path="whatsapp" element={<Suspense fallback={<Loading />}><WhatsAppSettings /></Suspense>} />
              <Route path="settings" element={<Suspense fallback={<Loading />}><Settings /></Suspense>} />
              <Route path="staff" element={<Suspense fallback={<Loading />}><Staff /></Suspense>} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </AdminLayout>
        </ProtectedRoute>
      } />

      {/* Technician */}
      <Route path="/technicien/*" element={
        <ProtectedRoute allowedRoles={['technicien', 'dentiste', 'assistante']}>
          <TechnicianLayout>
            <Routes>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Suspense fallback={<Loading />}><TechnicianDashboard /></Suspense>} />
              <Route path="rdv" element={<Suspense fallback={<Loading />}><Appointments /></Suspense>} />
              <Route path="patients" element={<Suspense fallback={<Loading />}><Patients /></Suspense>} />
              <Route path="odontogram" element={<Suspense fallback={<Loading />}><OdontogramView /></Suspense>} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </TechnicianLayout>
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function Loading() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: 80, gap: 16
    }}>
      <div style={{
        width: 40, height: 40, border: '3px solid #e2e8f0',
        borderTopColor: '#2563eb', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <p style={{ fontSize: 14, color: '#64748b', margin: 0, fontWeight: 500 }}>Chargement du module...</p>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <AppRoutes />
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;